import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { Box, CircularProgress } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { getAppTheme } from './theme';
import { clearSession, persistSessionMetadata, setTokens } from './utils/auth';
import { getApiBaseUrl } from './utils/apiBaseUrl';
import axios from 'axios';
import Navbar from './components/Navbar';
import Register from './pages/Register';
import Login from './pages/Login';
import VerifyEmail from './pages/VerifyEmail';
import ForgotPassword from './pages/ForgotPassword';
import Notifications from './pages/Notifications';
import KYCVerification from './pages/KYCVerification';
import ProfileSettings from './pages/ProfileSettings';
import PayoutProfile from './pages/Payment/PayoutProfile';
import PostRequirement from './pages/Buyer/PostRequirement';
import MyRequirements from './pages/Buyer/MyRequirements';
import ViewBids from './pages/Buyer/ViewBids';
import BidderProfile from './pages/Buyer/BidderProfile';
import ViewRequirements from './pages/Bidder/ViewRequirements';
import SubmitBid from './pages/Bidder/SubmitBid';
import BidderEarnings from './pages/Bidder/BidderEarnings';
import DealDetail from './pages/Deal/DealDetail';
import MyDeals from './pages/Deals/MyDeals';
import EscrowSetup from './pages/Escrow/EscrowSetup';
import SubmitRating from './pages/Ratings/SubmitRating';
import RaiseDispute from './pages/Dispute/RaiseDispute';
import ArbitrationRoom from './pages/Dispute/ArbitrationRoom';
import MyArbitrations from './pages/Dispute/MyArbitrations';
import TrustCenter from './pages/Trust/TrustCenter';
import AdminDeals from './pages/Admin/AdminDeals';
import AdminDisputes from './pages/Admin/AdminDisputes';
import AdminArbitrators from './pages/Admin/AdminArbitrators';
import ArbitratorKYC from './pages/Arbitrator/ArbitratorKYC';
import LandingPage from './pages/LandingPage';

export default function App() {
  const [themeMode, setThemeMode] = React.useState(localStorage.getItem('cmp_theme_mode') || 'dark');

  React.useEffect(() => {
    if (themeMode === 'light') {
      document.body.classList.add('light-theme');
    } else {
      document.body.classList.remove('light-theme');
    }

    const handleThemeChange = () => {
      const mode = localStorage.getItem('cmp_theme_mode') || 'dark';
      setThemeMode(mode);
    };

    window.addEventListener('cmp-theme-change', handleThemeChange);
    return () => window.removeEventListener('cmp-theme-change', handleThemeChange);
  }, [themeMode]);

  const currentTheme = React.useMemo(() => getAppTheme(themeMode), [themeMode]);

  return (
    <ThemeProvider theme={currentTheme}>
      <CssBaseline />
      <BrowserRouter>
        <AppShell />
      </BrowserRouter>
    </ThemeProvider>
  );
}

/**
 * AppShell lives inside BrowserRouter so it can call useLocation.
 * Hides the global MUI Navbar on the landing page (which has its own).
 */
function AppShell() {
  const location = useLocation();
  const isLanding = location.pathname === '/';
  const isPublicRoute = ['/', '/login', '/register', '/verify-email', '/forgot-password'].includes(location.pathname);
  const [restoringSession, setRestoringSession] = React.useState(!isPublicRoute);

  React.useEffect(() => {
    const restoreSession = async () => {
      if (isPublicRoute) {
        setRestoringSession(false);
        return;
      }
      try {
        // Use raw axios so a failed restore doesn't trigger the global interceptor redirect.
        const baseURL = getApiBaseUrl();
        const { data } = await axios.post(
          `${baseURL}/api/v1/auth/token/refresh/`,
          {},
          { withCredentials: true },
        );
        setTokens({ access: data.access });
        persistSessionMetadata(data);
      } catch {
        clearSession();
      } finally {
        setRestoringSession(false);
      }
    };
    restoreSession();
  }, [isPublicRoute]);

  if (restoringSession) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#080d1a' }}>
        <CircularProgress aria-label="Restoring session" />
      </Box>
    );
  }

  return (
    <>
      {!isLanding && <Navbar />}
      <Routes>
        {/* ── Public landing ── */}
        <Route path="/" element={<LandingPage />} />

        {/* ── Auth ── */}
        <Route path="/register"        element={<Register />} />
        <Route path="/login"           element={<Login />} />
        <Route path="/verify-email"    element={<VerifyEmail />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />

        {/* ── KYC ── */}
        <Route path="/kyc"        element={<Navigate to="/profile/settings?verify=true" replace />} />
        <Route path="/kyc/verify" element={<Navigate to="/profile/settings?verify=true" replace />} />

        {/* ── Notifications ── */}
        <Route path="/notifications" element={<Notifications />} />

        {/* ── Account ── */}
        <Route path="/profile/settings"       element={<ProfileSettings />} />
        <Route path="/account/payout-profile" element={<PayoutProfile />} />
        <Route path="/account/trust"          element={<TrustCenter />} />

        {/* ── Buyer ── */}
        <Route path="/buyer/requirements"          element={<MyRequirements />} />
        <Route path="/buyer/requirements/new"      element={<PostRequirement />} />
        <Route path="/buyer/requirements/:id/bids" element={<ViewBids />} />
        <Route path="/buyer/bidders/:anonymousId"   element={<BidderProfile />} />

        {/* ── Bidder ── */}
        <Route path="/bidder/requirements"         element={<ViewRequirements />} />
        <Route path="/bidder/requirements/:id/bid" element={<SubmitBid />} />
        <Route path="/bidder/earnings"             element={<BidderEarnings />} />

        {/* ── Deals ── */}
        <Route path="/deals/mine"       element={<MyDeals />} />
        <Route path="/deals/:id"        element={<DealDetail />} />
        <Route path="/deals/:id/escrow" element={<EscrowSetup />} />

        {/* ── Ratings & Disputes ── */}
        <Route path="/ratings/submit/:dealId" element={<SubmitRating />} />
        <Route path="/disputes/raise/:dealId" element={<RaiseDispute />} />
        <Route path="/arbitrations/:id"       element={<ArbitrationRoom />} />

        {/* ── Admin ── */}
        <Route path="/admin/deals" element={<AdminDeals />} />
        <Route path="/admin/disputes" element={<AdminDisputes />} />
        <Route path="/admin/arbitrators" element={<AdminArbitrators />} />
        
        {/* ── Arbitrator ── */}
        <Route path="/arbitrations/mine" element={<MyArbitrations />} />
        <Route path="/arbitrator/kyc" element={<Navigate to="/profile/settings?verify=true" replace />} />
      </Routes>
    </>
  );
}
